import { ReactNode } from 'react'
import { AuthState as OktaAuthState, CustomUserClaim as OktaCustomUserClaim, UserClaims as OktaUserClaims, CustomUserClaims as OktaCustomUserClaims } from '@okta/okta-auth-js'

import AuthenticationError from './errors/AuthenticationError'
import AuthorizationError from './errors/AuthorizationError'

export interface AuthenticationProviderProps {
  children: ReactNode
  caller?: string
}

export interface IMkUserClaims extends OktaUserClaims<OktaCustomUserClaims> {
  type?: string | OktaCustomUserClaim
  tier?: string | OktaCustomUserClaim
  email?: string
  email_verified?: boolean
  family_name?: string
  given_name?: string
  name?: string
  preferred_username?: string
  [key: string]: any
}

export interface IMkUser extends IMkUserClaims {
  memberNumber: string
  username: string
  email: string
  first: string
  last: string
  tier: string
  thumbnail?: string
  totalCredits: number | string
  availableCredits: number | string
  useByDate: string
  totalPoints: number | string
  availablePoints: number | string
  errorCode?: string
  errorMessage?: string
  brandId?: string
  accessCode?: string
  memberAccessToken?: string
  jwtToken?: string
}

export interface IMkAuthenticatedSession {
  readonly sessionType: 'authenticated'
  readonly authBrand: string
  readonly authenticatedUser: Record<string, any>
  readonly codeVerifier: string
  readonly username: string
  readonly sessionToken: string
  readonly recoveryToken: string
}

export interface IMkAuthorizedSession {
  readonly sessionType: 'authorized'
  readonly authBrand: string
  readonly authorizedUser: IMkUser
  readonly authenticatedUserName: string
  readonly sessionToken: string
  readonly recoveryToken: string
  readonly accessCode?: string
  readonly jwtToken?: string
  readonly memberAccessToken?: string
  readonly accessToken?: {
    accessToken: string
    claims: {
      sub: string
      [key: string]: any
    }
    expiresAt: number
    tokenType: string
    scopes: string[]
    authorizeUrl: string
    userinfoUrl: string
  }
  readonly idToken?: {
    idToken: string
    claims: {
      sub: string
      [key: string]: any
    }
    expiresAt: number
    scopes: string[]
    authorizeUrl: string
    issuer: string
    clientId: string
  }
}

export type TMkSession = IMkAuthenticatedSession | IMkAuthorizedSession

export interface IMkAuthenticationState extends OktaAuthState {
  readonly error: Error | undefined
  readonly isAuthenticated: boolean
  readonly isAuthorized: boolean
  readonly isAuthInitialized: boolean
  readonly isLoggingOut: boolean
  readonly loginOrigin: string | null
  readonly session: TMkSession | undefined
  readonly failedLoginAttempts: number
  readonly failedMfaAttempts: number
  readonly isAccountLocked: boolean
  readonly isMfaLocked: boolean
  readonly lockoutEndTime: number | null
  readonly mfaLockoutEndTime: number | null
  readonly lockedUsername: string | null
}

export type TMkAuthenticationState = IMkAuthenticationState

export enum EMkAuthenticationAction {
  AUTHENTICATION_FAILURE = 'AUTHENTICATION_FAILURE',
  AUTHENTICATION_REQUEST = 'AUTHENTICATION_REQUEST',
  AUTHENTICATION_SUCCESS = 'AUTHENTICATION_SUCCESS',
  AUTHORIZATION_FAILURE = 'AUTHORIZATION_FAILURE',
  AUTHORIZATION_REQUEST = 'AUTHORIZATION_REQUEST',
  AUTHORIZATION_SUCCESS = 'AUTHORIZATION_SUCCESS',
  AUTHORIZATION_REFRESH = 'AUTHORIZATION_REFRESH',
  CLEAR_ERROR = 'CLEAR_ERROR',
  LOGIN_FAILURE = 'LOGIN_FAILURE',
  START_LOGOUT = 'START_LOGOUT',
  LOGOUT = 'LOGOUT',
  LOGOUT_WITH_ERROR = 'LOGOUT_WITH_ERROR',
  REFRESH_AUTHENTICATION = 'REFRESH_AUTHENTICATION',
  REGISTER_FAILURE = 'REGISTER_FAILURE',
  REGISTER_REQUEST = 'REGISTER_REQUEST',
  REGISTER_SUCCESS = 'REGISTER_SUCCESS',
  REMOVE_AUTHENTICATION_ORIGIN = 'REMOVE_AUTHENTICATION_ORIGIN',
  REVOKE_AUTHENTICATION = 'REVOKE_AUTHENTICATION',
  SET_AUTHENTICATION_ORIGIN = 'SET_AUTHENTICATION_ORIGIN',
  SESSION_RENEWED = 'SESSION_RENEWED',
  INCREMENT_FAILED_ATTEMPTS = 'INCREMENT_FAILED_ATTEMPTS',
  INCREMENT_FAILED_MFA_ATTEMPTS = 'INCREMENT_FAILED_MFA_ATTEMPTS',
  RESET_FAILED_ATTEMPTS = 'RESET_FAILED_ATTEMPTS',
  RESET_FAILED_MFA_ATTEMPTS = 'RESET_FAILED_MFA_ATTEMPTS',
  LOCK_ACCOUNT = 'LOCK_ACCOUNT',
  LOCK_MFA_ACCOUNT = 'LOCK_MFA_ACCOUNT',
  UNLOCK_ACCOUNT = 'UNLOCK_ACCOUNT',
  UNLOCK_MFA_ACCOUNT = 'UNLOCK_MFA_ACCOUNT',
  RESET_STATE = 'RESET_STATE',
}

export type TMkAuthenticationAction =
  | { type: EMkAuthenticationAction.AUTHENTICATION_FAILURE; payload: AuthenticationError }
  | { type: EMkAuthenticationAction.AUTHENTICATION_REQUEST }
  | { type: EMkAuthenticationAction.AUTHORIZATION_REFRESH }
  | { type: EMkAuthenticationAction.AUTHENTICATION_SUCCESS; payload?: IMkAuthenticatedSession }
  | { type: EMkAuthenticationAction.AUTHORIZATION_FAILURE; payload: AuthorizationError }
  | { type: EMkAuthenticationAction.AUTHORIZATION_SUCCESS; payload?: IMkAuthorizedSession }
  | { type: EMkAuthenticationAction.AUTHORIZATION_REQUEST }
  | { type: EMkAuthenticationAction.CLEAR_ERROR }
  | { type: EMkAuthenticationAction.START_LOGOUT }
  | { type: EMkAuthenticationAction.LOGOUT }
  | { type: EMkAuthenticationAction.LOGOUT_WITH_ERROR; payload: Error }
  | { type: EMkAuthenticationAction.REGISTER_FAILURE; payload: Error }
  | { type: EMkAuthenticationAction.REGISTER_REQUEST }
  | { type: EMkAuthenticationAction.REGISTER_SUCCESS; payload: IMkUserClaims }
  | { type: EMkAuthenticationAction.REMOVE_AUTHENTICATION_ORIGIN }
  | { type: EMkAuthenticationAction.REVOKE_AUTHENTICATION }
  | { type: EMkAuthenticationAction.SET_AUTHENTICATION_ORIGIN; payload: string }
  | { type: EMkAuthenticationAction.SESSION_RENEWED }
  | { type: EMkAuthenticationAction.INCREMENT_FAILED_ATTEMPTS }
  | { type: EMkAuthenticationAction.INCREMENT_FAILED_MFA_ATTEMPTS }
  | { type: EMkAuthenticationAction.RESET_FAILED_ATTEMPTS }
  | { type: EMkAuthenticationAction.RESET_FAILED_MFA_ATTEMPTS }
  | { type: EMkAuthenticationAction.LOCK_ACCOUNT; payload: { lockoutEndTime: number; username: string } }
  | { type: EMkAuthenticationAction.LOCK_MFA_ACCOUNT; payload: { lockoutEndTime: number; username: string } }
  | { type: EMkAuthenticationAction.UNLOCK_ACCOUNT }
  | { type: EMkAuthenticationAction.UNLOCK_MFA_ACCOUNT }
  | { type: EMkAuthenticationAction.RESET_STATE }
